import { useEffect, useState } from 'react';
import { Pause, Timer } from 'lucide-react';
import { formatMinutes } from '@homework/shared';
import { useRealtime } from '../lib/realtime';
import { cx } from './ui';

/**
 * The running counter from FR-3.1. The server is authoritative and pushes a
 * snapshot every 15s; between snapshots the clock advances locally so the
 * number does not visibly freeze, and it stops advancing while paused (Away).
 */
export function SessionTimer({ compact = false }: { compact?: boolean }) {
  const { timer } = useRealtime();
  const [driftMinutes, setDriftMinutes] = useState(0);

  useEffect(() => {
    setDriftMinutes(0);
    if (!timer || timer.paused) return;
    const asOf = new Date(timer.asOf).getTime();
    const interval = setInterval(() => {
      setDriftMinutes((Date.now() - asOf) / 60_000);
    }, 10_000);
    return () => clearInterval(interval);
  }, [timer]);

  if (!timer) {
    return <span className="text-sm text-slate-400">—</span>;
  }

  const sessionMinutes = timer.sessionMinutes + (timer.paused ? 0 : driftMinutes);
  const todayMinutes = timer.todayMinutes + (timer.paused ? 0 : driftMinutes);

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1.5 tabular-nums"
        title={`Today ${formatMinutes(todayMinutes)} · This week ${formatMinutes(timer.weekMinutes)}`}
      >
        {timer.paused ? (
          <Pause className="h-3.5 w-3.5 text-slate-400" aria-hidden />
        ) : (
          <Timer className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
        )}
        {formatMinutes(sessionMinutes)}
      </span>
    );
  }

  return (
    <div
      className={cx(
        'flex shrink-0 items-center gap-3 whitespace-nowrap rounded-full px-3.5 py-1.5 ring-1 ring-inset',
        timer.paused
          ? 'bg-slate-400/10 ring-slate-400/25'
          : 'bg-emerald-400/15 ring-emerald-500/25',
      )}
      aria-live="off"
    >
      {timer.paused ? (
        <Pause className="h-4 w-4 text-slate-400" aria-hidden />
      ) : (
        <Timer className="h-4 w-4 text-emerald-600" aria-hidden />
      )}
      <div className="leading-tight">
        <p className="text-sm font-bold tabular-nums text-slate-900">
          {formatMinutes(sessionMinutes)}
          <span className="ml-1.5 text-xs font-medium text-slate-500">
            {timer.paused ? 'paused' : 'this session'}
          </span>
        </p>
        <p className="text-xs tabular-nums text-slate-500">
          Today {formatMinutes(todayMinutes)} · Week {formatMinutes(timer.weekMinutes)}
        </p>
      </div>
    </div>
  );
}
